// Kept in sync with the backend's TYPING_KINDS whitelist (gateway.ts) -
// anything else the server receives gets dropped before it's ever relayed,
// so this union only needs to describe what can actually arrive.
export type TypingKind = "image" | "video" | "media" | "file" | "voice" | "recording";

import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getAccessToken, API_URL, api } from "./api";
import { encryptForConversation } from "./e2ee";
import { useAuth } from "./auth-context";
import type { Message, AppNotification, ConversationSummary } from "@/types";
import { toast } from "sonner";
import { playNotificationSound, type NotificationSoundName } from "./notificationSound";
import i18n from "./i18n";
import { removeCachedMessage } from "./local-store";

// Carried opaquely - the backend never inspects SDP/ICE content, it just relays
// it, so the frontend doesn't need a WsEvent-level shape for it either.
export type CallSignalType = "call:offer" | "call:answer" | "call:ice-candidate" | "call:end" | "call:reject" | "call:ringing";
export interface CallSignalEvent {
  type: CallSignalType;
  conversationId: string;
  fromUserId: string;
  payload: unknown;
}

// Watch-together's own signal shape - structurally identical to
// CallSignalEvent (same pairwise toUserId/conversationId/payload relay,
// same backend gateway machinery), kept as a separate type only because a
// watch signal and a call signal are semantically different events even
// though the wire shape happens to match.
export type WatchSignalType = "watch:invite" | "watch:accept" | "watch:decline" | "watch:sync" | "watch:end";
export interface WatchSignalEvent {
  type: WatchSignalType;
  conversationId: string;
  fromUserId: string;
  payload: unknown;
}

// Group calling's own signal shapes - deliberately separate from
// CallSignalEvent above rather than shoehorned into it, since these aren't
// pairwise (no fromUserId/toUserId the same way) and carry different data
// (a participant list, not an SDP/ICE payload).
export interface GroupCallJoinedEvent {
  type: "call:group-joined";
  conversationId: string;
  participants: string[];
}
export interface GroupCallParticipantEvent {
  type: "call:participant-joined" | "call:participant-left";
  conversationId: string;
  userId: string;
}
export type GroupCallSignalEvent = GroupCallJoinedEvent | GroupCallParticipantEvent;

// Trusted-device approval events (login_challenge, sync_request). Same
// reasoning as CallSignalEvent/WatchSignalEvent above: an approval request
// can arrive while the person is anywhere in the app, not just a specific
// settings screen. Two backend-distinct event types (a login challenge
// never transfers identity; a sync request always does) are unified into
// one listener stream since both present the same underlying UX.
export interface LoginChallengeEvent {
  type: "login_challenge";
  id: string;
  country: string | null;
  userAgent: string | null;
  expiresAt: string;
}
export interface SyncRequestEvent {
  type: "sync_request";
  id: string;
  deviceLabel: string | null;
  ephemeralPublicKey: string;
  expiresAt: string;
}
export type TrustedDeviceRequestEvent = LoginChallengeEvent | SyncRequestEvent;

type WsEvent =
  | { type: "message:new"; message: Message }
  | { type: "message:edited"; message: Message }
  | { type: "message:deleted"; conversationId: string; messageId: string }
  | { type: "message:reaction"; conversationId: string; messageId: string; userId: string; emoji: string | null }
  | { type: "read:update"; conversationId: string; userId: string; messageId: string }
  | { type: "delivered:update"; conversationId: string; userId: string; messageId: string }
  | { type: "typing"; conversationId: string; userId: string; kind?: TypingKind }
  | { type: "notification:new"; notification: AppNotification }
  | { type: "presence:update"; userId: string; isOnline: boolean }
  | CallSignalEvent
  | GroupCallSignalEvent
  | WatchSignalEvent
  | LoginChallengeEvent
  | SyncRequestEvent
  | { type: "pong" };

function isCallSignalEvent(event: WsEvent): event is CallSignalEvent {
  return (
    event.type === "call:offer" ||
    event.type === "call:answer" ||
    event.type === "call:ice-candidate" ||
    event.type === "call:end" ||
    event.type === "call:reject" ||
    event.type === "call:ringing"
  );
}

function isWatchSignalEvent(event: WsEvent): event is WatchSignalEvent {
  return (
    event.type === "watch:invite" ||
    event.type === "watch:accept" ||
    event.type === "watch:decline" ||
    event.type === "watch:sync" ||
    event.type === "watch:end"
  );
}

function isGroupCallSignalEvent(event: WsEvent): event is GroupCallSignalEvent {
  return event.type === "call:group-joined" || event.type === "call:participant-joined" || event.type === "call:participant-left";
}

function isTrustedDeviceRequestEvent(event: WsEvent): event is TrustedDeviceRequestEvent {
  return event.type === "login_challenge" || event.type === "sync_request";
}

// Same reasoning as callSignalListeners below - can arrive anywhere in the
// app, so this needs its own subscription point outside any one screen's
// component tree.
const trustedDeviceRequestListeners = new Set<(event: TrustedDeviceRequestEvent) => void>();
export function onTrustedDeviceRequest(listener: (event: TrustedDeviceRequestEvent) => void) {
  trustedDeviceRequestListeners.add(listener);
  return () => {
    trustedDeviceRequestListeners.delete(listener);
  };
}

// Tracks sync_request ids this tab itself created, so it never prompts
// itself to approve its own request. publishToUser broadcasts to every
// connected session for a user, including the exact socket that just made
// the HTTP call that created the request - HTTP and WebSocket are separate
// channels with no inherent link between them, so the backend has no way to
// exclude "the caller" from that broadcast. This is the client-side fix.
const ownSyncRequestIds = new Set<string>();
export function registerOwnSyncRequest(id: string) {
  ownSyncRequestIds.add(id);
}

// A call can come in while the person is anywhere in the app, not just on
// ChatPage - so, like openConversationId below, call signaling needs a
// subscription point that isn't scoped to any one screen. CallProvider (mounted
// once, high in the tree) is the only real subscriber; a plain module-level set
// is enough since there's only ever one live subscriber in practice, and this
// avoids threading call state through RealtimeContext/React Query for something
// that's really just "forward this WS message to whoever's listening".
const callSignalListeners = new Set<(event: CallSignalEvent) => void>();
export function onCallSignal(listener: (event: CallSignalEvent) => void) {
  callSignalListeners.add(listener);
  return () => {
    callSignalListeners.delete(listener);
  };
}

// Same reasoning as callSignalListeners above - watch-together can be
// invited into from anywhere in the app, not just an already-open chat.
const watchSignalListeners = new Set<(event: WatchSignalEvent) => void>();
export function onWatchSignal(listener: (event: WatchSignalEvent) => void) {
  watchSignalListeners.add(listener);
  return () => {
    watchSignalListeners.delete(listener);
  };
}

// Same reasoning as callSignalListeners above - a group call can be joined
// from anywhere in the app, so this needs its own subscription point outside
// any one screen's component tree.
const groupCallSignalListeners = new Set<(event: GroupCallSignalEvent) => void>();
export function onGroupCallSignal(listener: (event: GroupCallSignalEvent) => void) {
  groupCallSignalListeners.add(listener);
  return () => {
    groupCallSignalListeners.delete(listener);
  };
}

interface RealtimeContextValue {
  sendTyping: (conversationId: string, kind?: TypingKind) => void;
  sendCallSignal: (event: { type: CallSignalType; toUserId: string; conversationId: string; payload: unknown }) => void;
  sendWatchSignal: (event: { type: WatchSignalType; toUserId: string; conversationId: string; payload: unknown }) => void;
  sendGroupCallSignal: (event: { type: "call:join-group" | "call:leave-group"; conversationId: string }) => void;
}

const RealtimeContext = createContext<RealtimeContextValue>({
  sendTyping: () => {},
  sendCallSignal: () => {},
  sendWatchSignal: () => {},
  sendGroupCallSignal: () => {}
});

// Set by ChatPage on mount/unmount. A plain module-level value (not React state) is
// enough here - it's read once per incoming WS event, not rendered anywhere itself.
let openConversationId: string | null = null;
export function setOpenConversationId(id: string | null) {
  openConversationId = id;
}

// Tracks the last time THIS device auto-replied to each conversation, so vacation
// mode doesn't spam the same person repeatedly. This is also what keeps two
// people who BOTH have vacation mode on from looping forever: A messages B, B
// auto-replies once (and starts its own cooldown for that conversation), A's
// client sees B's reply as a new incoming message and - since A hadn't yet
// auto-replied to B in this conversation - sends its own one auto-reply back,
// which starts A's cooldown too. From that point neither side will fire again
// until the cooldown expires, so the worst case is exactly one reply from each
// side, never an unbounded loop - checked and set synchronously, before any
// async work, specifically so a burst of rapid messages can't race past it.
const vacationReplyCooldown = new Map<string, number>();
const VACATION_REPLY_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour

async function maybeSendVacationAutoReply(message: Message, user: { id: string; vacation_mode_enabled: boolean; vacation_message: string | null }) {
  if (!user.vacation_mode_enabled || !user.vacation_message) return;
  if (message.sender_id === user.id) return; // never reply to my own messages from another device

  const lastReplied = vacationReplyCooldown.get(message.conversation_id) ?? 0;
  if (Date.now() - lastReplied < VACATION_REPLY_COOLDOWN_MS) return;
  vacationReplyCooldown.set(message.conversation_id, Date.now()); // set BEFORE sending, not after

  try {
    const { content, nonce, keyEpoch } = await encryptForConversation(message.conversation_id, message.key_epoch, user.vacation_message);
    await api.post(`/api/conversations/${message.conversation_id}/messages`, { content, nonce, keyEpoch });
  } catch {
    // Best-effort - a missed auto-reply isn't worth surfacing an error for, and
    // retrying automatically risks exactly the spam this cooldown exists to avoid.
  }
}

const ALERT_MESSAGES: Partial<Record<AppNotification["type"], (name: string) => string>> = {
  message: (name) => `New message from ${name}`,
  friend_request: (name) => `${name} sent you a friend request`,
  friend_accept: (name) => `${name} accepted your friend request`,
  follow_request: (name) => `${name} requested to follow you`,
  new_follower: (name) => `${name} started following you`,
  follow_accept: (name) => `${name} accepted your follow request`,
  post_like: (name) => `${name} liked your post`,
  post_comment: (name) => `${name} commented on your post`,
  skot_received: (name) => i18n.t("notifications.skotReceivedBody", { name }),
  skot_requested: (name) => i18n.t("notifications.skotRequestedBody", { name }),
  skot_request_declined: (name) => i18n.t("notifications.requestDeclinedBody", { name })
};

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>();
  const lastTypingSentAt = useRef<Record<string, number>>({});

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    function connect() {
      const token = getAccessToken();
      if (!token || cancelled) return;

      const wsUrl = API_URL.replace(/^http/, "ws");
      const socket = new WebSocket(`${wsUrl}/ws?token=${encodeURIComponent(token)}`);
      socketRef.current = socket;
      const pingInterval = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: "ping" }));
      }, 30_000);

      socket.onmessage = (event) => {
        const data: WsEvent = JSON.parse(event.data);

        if (data.type === "message:new") {
          queryClient.invalidateQueries({ queryKey: ["messages", data.message.conversation_id] });
          queryClient.invalidateQueries({ queryKey: ["conversations"] });
          if (data.message.sender_id !== user?.id) {
            api.post(`/api/conversations/${data.message.conversation_id}/delivered`, { messageId: data.message.id }).catch(() => {
              // best-effort - a missed delivery mark just means the tick stays at "sent"
              // a little longer, never a functional problem worth surfacing
            });
            if (user) maybeSendVacationAutoReply(data.message, user);

            // Sound plays only for a conversation the person ISN'T currently
            // looking at (openConversationId), and only if it isn't muted -
            // reads the already-cached conversation list rather than a fresh
            // fetch, since these preference fields can't have changed as a
            // result of this message arriving.
            if (data.message.conversation_id !== openConversationId) {
              const conversations = queryClient.getQueryData<ConversationSummary[]>(["conversations"]);
              const convo = conversations?.find((c) => c.id === data.message.conversation_id);
              const isMuted = convo?.is_muted || (!!convo?.muted_until && new Date(convo.muted_until) > new Date());
              if (!isMuted) {
                playNotificationSound((convo?.notification_sound as NotificationSoundName | null) ?? "default");
              }
            }
          }
        }

        if (data.type === "message:edited") {
          queryClient.invalidateQueries({ queryKey: ["messages", data.message.conversation_id] });
          queryClient.invalidateQueries({ queryKey: ["conversations"] });
        }

        if (data.type === "message:deleted") {
          // Not just invalidate-and-refetch - actively remove it from the
          // local on-device cache too, the same fix applied to the delete
          // mutations themselves (see useConversations.ts). This handler is
          // what a tab/device relies on when a message was deleted
          // elsewhere (another tab, another device, someone else in a
          // group) rather than by this tab's own action - without this,
          // that tab's local cache would still hold the deleted message,
          // and its next refresh would flash it again before the server
          // reconciliation caught up.
          removeCachedMessage(data.conversationId, data.messageId).catch(() => {});
          queryClient.invalidateQueries({ queryKey: ["messages", data.conversationId] });
        }

        if (data.type === "message:reaction") {
          queryClient.invalidateQueries({ queryKey: ["messages", data.conversationId] });
        }

        if (data.type === "read:update") {
          queryClient.invalidateQueries({ queryKey: ["read-status", data.conversationId] });
        }

        if (data.type === "delivered:update") {
          queryClient.invalidateQueries({ queryKey: ["delivery-status", data.conversationId] });
        }

        if (data.type === "presence:update") {
          queryClient.setQueriesData<ConversationSummary[]>({ predicate: (q) => q.queryKey[0] === "conversations" }, (old) =>
            old?.map((c) =>
              c.other_user?.id === data.userId ? { ...c, other_user: { ...c.other_user, is_online: data.isOnline } } : c
            )
          );
        }

        if (data.type === "notification:new") {
          queryClient.invalidateQueries({ queryKey: ["notifications"] });

          const isForOpenConversation =
            data.notification.type === "message" &&
            (data.notification.data as { conversationId?: string }).conversationId === openConversationId;

          const buildMessage = ALERT_MESSAGES[data.notification.type];
          if (buildMessage && !isForOpenConversation) {
            const name = data.notification.actor_display_name ?? "Someone";
            toast(buildMessage(name));
          }
        }

        if (data.type === "typing") {
          // Keyed by userId (not overwritten wholesale) so multiple people typing
          // at once in a group don't stomp on each other - each sender's own ping
          // only ever touches their own entry. Same 4s "still typing" staleness
          // window as before, now applied per-user at read time. Stores kind
          // alongside the timestamp (not just the timestamp) so a receiver can
          // render "Recording…" / "Voice delivering…" etc, not just "typing…".
          const prev = queryClient.getQueryData<Record<string, TypingEntry>>(["typing", data.conversationId]) ?? {};
          queryClient.setQueryData(["typing", data.conversationId], {
            ...prev,
            [data.userId]: { at: Date.now(), kind: data.kind }
          });
        }

        if (isCallSignalEvent(data)) {
          for (const listener of callSignalListeners) listener(data);
        }

        if (isWatchSignalEvent(data)) {
          for (const listener of watchSignalListeners) listener(data);
        }

        if (isGroupCallSignalEvent(data)) {
          for (const listener of groupCallSignalListeners) listener(data);
        }

        if (isTrustedDeviceRequestEvent(data)) {
          const isOwnSyncRequest = data.type === "sync_request" && ownSyncRequestIds.has(data.id);
          if (!isOwnSyncRequest) {
            for (const listener of trustedDeviceRequestListeners) listener(data);
          }
        }
      };

      socket.onclose = () => {
        clearInterval(pingInterval);
        if (!cancelled) reconnectTimer.current = setTimeout(connect, 2000);
      };
    }

    // Deferred by one tick: React 18 StrictMode (dev only) mounts, cleans up, and
    // re-mounts every effect once to catch missing-cleanup bugs. Opening the
    // WebSocket synchronously here means the first mount's socket gets closed mid
    // handshake by that simulated cleanup - harmless, but it prints a scary-looking
    // "WebSocket is closed before the connection is established" warning and wastes
    // a connection. Deferring means the simulated cleanup cancels the pending
    // timeout before any socket exists at all, so only the real mount ever connects.
    const startTimer = setTimeout(connect, 0);

    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      clearTimeout(reconnectTimer.current);
      socketRef.current?.close();
    };
  }, [user, queryClient]);

  // Throttled to once per 2s per conversation - firing on every keystroke would
  // spam the socket and every other participant's UI for no benefit. kind is
  // optional and purely descriptive (what's currently happening - typing text,
  // uploading a photo, recording voice, etc) - omitting it is still a plain
  // "typing…" signal exactly as before.
  function sendTyping(conversationId: string, kind?: TypingKind) {
    const now = Date.now();
    const last = lastTypingSentAt.current[conversationId] ?? 0;
    if (now - last < 2000) return;
    lastTypingSentAt.current[conversationId] = now;

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: "typing", conversationId, ...(kind ? { kind } : {}) }));
    }
  }

  // Unthrottled, unlike sendTyping - call signaling needs every offer/answer/ICE
  // candidate/end/reject/ringing message to go through immediately, not be
  // dropped by a 2s window meant for a completely different, high-frequency,
  // lossy-is-fine event type.
  function sendCallSignal(event: { type: CallSignalType; toUserId: string; conversationId: string; payload: unknown }) {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(event));
    }
  }

  function sendWatchSignal(event: { type: WatchSignalType; toUserId: string; conversationId: string; payload: unknown }) {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(event));
    }
  }

  function sendGroupCallSignal(event: { type: "call:join-group" | "call:leave-group"; conversationId: string }) {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(event));
    }
  }

  return <RealtimeContext.Provider value={{ sendTyping, sendCallSignal, sendWatchSignal, sendGroupCallSignal }}>{children}</RealtimeContext.Provider>;
}

export function useSendCallSignal() {
  return useContext(RealtimeContext).sendCallSignal;
}

export function useSendWatchSignal() {
  return useContext(RealtimeContext).sendWatchSignal;
}

export function useSendGroupCallSignal() {
  return useContext(RealtimeContext).sendGroupCallSignal;
}

export function useSendTyping() {
  return useContext(RealtimeContext).sendTyping;
}

export interface TypingEntry { at: number; kind?: TypingKind }
export interface TypingUser { id: string; kind?: TypingKind }

export function useTypingIndicator(conversationId: string): TypingUser[] {
  const queryClient = useQueryClient();
  const data = queryClient.getQueryData<Record<string, TypingEntry>>(["typing", conversationId]);
  if (!data) return [];
  const now = Date.now();
  return Object.entries(data)
    .filter(([, entry]) => now - entry.at <= 4000)
    .map(([userId, entry]) => ({ id: userId, kind: entry.kind }));
}
